<!-- Fixed NewChatScreen.vue with FAQ navigation -->
<template>
  <div class="flex flex-col h-full bg-white relative">
    <!-- Chat Header -->
    <NewChatHeader
      :styles="styles"
      :isExpanded="isExpanded"
      :isMobile="localIsMobile"
      @change-screen="handleScreenChange"
      @expand-chat="handleExpandChat"
      @close-widget="handleCloseWidget"
      @end-chat="handleEndChatFromHeader"
    />

    <!-- Chat Body (Main Chat Area) - Always show -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <NewChatBody
        ref="chatBodyRef"
        :styles="styles"
        :companyId="companyId"
        :isExpanded="isExpanded"
        @form-submitted="handleFormSubmitted"
        @mood-submitted="handleMoodSubmitted"
        @chat-complete="handleChatComplete"
        @customer-changed="handleCustomerChanged"
        @conversation-changed="handleConversationChanged"
        @message-sent="handleMessageFromBody"
        @navigate-to-faq="handleFaqNavigation"
      />

      <!-- Input positioned normally when expanded -->
      <div v-if="isExpanded && showInput" class="flex-shrink-0">
        <NewChatInput
          :styles="styles"
          :customer="customer"
          :conversation="conversation"
          :disabled="conversationStatus === 'paused'"
          @message-sent="handleInputMessage"
        />
      </div>
    </div>

    <!-- Input positioned above navbar when not expanded and active -->
    <div
      v-if="showInput && !isExpanded"
      class="absolute bottom-18 left-0 right-0 z-50 bg-white border-b border-gray-200"
    >
      <NewChatInput
        :styles="styles"
        :customer="customer"
        :conversation="conversation"
        :disabled="conversationStatus === 'paused'"
        @message-sent="handleInputMessage"
      />
    </div>
  </div>
</template>

<script>
import NewChatHeader from "../components/chat/NewChatHeader.vue";
import NewChatBody from "../components/chat/NewChatBody.vue";
import NewChatInput from "../components/chat/NewChatInput.vue";
import { chatStateManager } from "../lib/chatStateManager";

export default {
  name: "NewChatScreen",
  components: {
    NewChatHeader,
    NewChatBody,
    NewChatInput,
  },
  props: {
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: "#3b82f6",
        companyName: "Chatly",
        companyLogo: null,
      }),
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: String,
      default: null,
    },
  },
  emits: [
    "change-screen",
    "expand-chat",
    "close-widget",
    "mood-submitted",
    "chat-complete",
    "navigate-to-faq", // 🆕 NEW: Add FAQ navigation emit
  ],
  data() {
    return {
      localIsMobile: false,
      chatStarted: true,
      conversationStatus: "active",

      // ✅ Local state from chat state manager
      customer: null,
      conversation: null,
      customerName: "",
      stateUnsubscribe: null,
    };
  },
  computed: {
    showInput() {
      const result =
        this.conversationStatus !== "ended" &&
        this.customer &&
        this.conversation;
      console.log("🔍 showInput check:", {
        conversationStatus: this.conversationStatus,
        hasCustomer: !!this.customer,
        hasConversation: !!this.conversation,
        result: result,
      });
      return result;
    },
  },
  watch: {
    // ✅ Watch for changes in global state
    $route() {
      this.syncWithGlobalState();
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);

    // ✅ Subscribe to global chat state
    this.stateUnsubscribe = chatStateManager.subscribe((newState) => {
      this.customer = newState.customer;
      this.conversation = newState.conversation;
      this.customerName = newState.customerName;
    });

    // ✅ Get initial state
    this.syncWithGlobalState();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
    if (this.stateUnsubscribe) {
      this.stateUnsubscribe();
    }
  },
  methods: {
    // ✅ NEW: Sync with global state
    syncWithGlobalState() {
      const state = chatStateManager.getState();
      this.customer = state.customer;
      this.conversation = state.conversation;
      this.customerName = state.customerName;

      console.log("📊 Customer data received in ChatScreen:", this.customer);
      console.log(
        "💬 Conversation data received in ChatScreen:",
        this.conversation
      );
    },

    checkMobile() {
      this.localIsMobile = window.innerWidth < 768;
    },

    handleScreenChange(screenName) {
      this.$emit("change-screen", screenName);
    },

    // In NewChatScreen.vue methods:
    handleExpandChat(expandedState) {
      // ✅ Pass the expanded state value through to the parent
      this.$emit("expand-chat", expandedState);
    },

    handleCloseWidget() {
      this.$emit("close-widget");
    },

    // 🆕 NEW: Handle FAQ navigation from NewChatBody
    handleFaqNavigation(faqEvent) {
      console.log('🎯 FAQ Navigation requested in NewChatScreen:', faqEvent);
      
      // Emit to parent (ChatWidget) to switch to FAQ screen
      this.$emit('navigate-to-faq', faqEvent);
    },

    // ✅ NEW: Handle end chat request from header
    async handleEndChatFromHeader() {
      console.log('🔚 End chat request received in NewChatScreen');
      
      // Call the end chat method on NewChatBody component
      if (this.$refs.chatBodyRef && this.$refs.chatBodyRef.handleEndChatFromHeader) {
        await this.$refs.chatBodyRef.handleEndChatFromHeader();
      } else {
        console.warn('⚠️ ChatBody ref not available or method not found');
        
        // Fallback: Ask user for confirmation directly
        const confirmed = confirm('Möchten Sie den Chat wirklich beenden?');
        if (confirmed) {
          console.log('✅ User confirmed chat end via fallback');
          // You could emit an event or handle this differently
        }
      }
    },

    handleFormSubmitted(data) {
      console.log("📋 Form submitted in ChatScreen:", data);
      this.syncWithGlobalState(); // Update local state after form submission
    },

    handleCustomerChanged(newCustomer) {
      this.customer = newCustomer;
    },

    handleConversationChanged(newConversation) {
      this.conversation = newConversation;
    },

    // ✅ FIXED: Handle messages from input and add to global state
    handleInputMessage(messageData) {
      console.log("📨 Message from input:", messageData);

      // ✅ Check if message already exists to prevent duplicates
      const currentState = chatStateManager.getState();
      const messageExists = currentState.messages.some(
        (msg) =>
          msg.content === messageData.message.content &&
          msg.type === "customer" &&
          Math.abs(
            new Date(msg.timestamp) - new Date(messageData.message.created_at)
          ) < 5000 // Within 5 seconds
      );

      if (messageExists) {
        console.log("🚫 Message already exists, skipping duplicate");
        return;
      }

      // ✅ Add the message to global state so ChatAI can see it
      chatStateManager.addMessage({
        id: messageData.message.$id, // Use the actual Appwrite ID
        type: "customer",
        content: messageData.message.content,
        timestamp: new Date(messageData.message.created_at),
        senderId: messageData.message.sender_id,
      });
    },

    // ✅ Handle messages from chat body
    handleMessageFromBody(messageData) {
      console.log("📨 Message from body:", messageData);
      // Body messages are already handled by the state manager
    },

    handleMoodSubmitted(moodData) {
      this.$emit("mood-submitted", moodData);
    },

    handleChatComplete() {
      this.$emit("chat-complete");
    },
  },
};
</script>